(() => {
  const normalise = (value) => (value || '')
    .toLocaleLowerCase('pt-PT')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const setRequired = (field, required) => {
    if (!field) return;
    field.required = required;
    field.setAttribute('aria-required', required ? 'true' : 'false');
  };

  const setConditionalField = (container, field, visible, required = false) => {
    if (container) container.hidden = !visible;
    if (!field) return;
    field.disabled = !visible;
    setRequired(field, visible && required);
  };

  const initContact = (root) => {
    const preferenceInputs = [...root.querySelectorAll('[data-contact-preference]')];
    const phoneField = root.querySelector('[data-contact-phone-field]');
    const phoneInput = phoneField?.querySelector('input');
    const subject = root.querySelector('[data-contact-subject]');
    const orderField = root.querySelector('[data-contact-order-field]');

    const updatePreference = () => {
      const preference = preferenceInputs.find((input) => input.checked)?.value || 'E-mail';
      const needsPhone = preference === 'Telefone' || preference === 'WhatsApp';
      setConditionalField(phoneField, phoneInput, needsPhone, true);
    };

    const updateSubject = () => {
      if (!orderField || !subject) return;
      const orderInput = orderField.querySelector('input');
      setConditionalField(orderField, orderInput, subject.value === 'Encomenda');
    };

    preferenceInputs.forEach((input) => input.addEventListener('change', updatePreference));
    subject?.addEventListener('change', updateSubject);
    updatePreference();
    updateSubject();
  };

  const initConversion = (root) => {
    const balanceInputs = [...root.querySelectorAll('[data-conversion-balance]')];
    const directionInputs = [...root.querySelectorAll('[data-conversion-direction]')];
    const cardField = root.querySelector('[data-conversion-card-field]');
    const cardInput = cardField?.querySelector('input');
    const accountField = root.querySelector('[data-conversion-account-field]');
    const accountInput = accountField?.querySelector('input');
    const physicalField = root.querySelector('[data-conversion-physical-field]');
    const physicalInput = physicalField?.querySelector('input');
    const routeInput = root.querySelector('[data-conversion-route-input]');
    const routeSummary = root.querySelector('[data-conversion-route-summary]');
    const summarySubmit = root.querySelector('[data-conversion-submit]');
    const postedSuccessfully = Boolean(root.querySelector('.bt-form-status--success'));
    const chooseMessage = root.dataset.routeChoose || '';
    const requirements = [...root.querySelectorAll('[data-conversion-requirement]')];
    const progressItems = [...root.querySelectorAll('[data-conversion-progress]')];

    const selectedValue = (inputs) => inputs.find((input) => input.checked)?.value || '';

    const routeLabel = (kind, direction) => {
      if (!kind || !direction) return chooseMessage;
      const key = `route${kind === 'gift' ? 'Gift' : 'Points'}${direction === 'Loja física → Loja online' ? 'ToOnline' : 'ToStore'}`;
      return root.dataset[key] || chooseMessage;
    };

    const update = () => {
      const selectedBalance = balanceInputs.find((input) => input.checked);
      const balance = selectedBalance?.value || '';
      // Keep older rendered forms working while their cached markup is replaced.
      const kind = selectedBalance?.dataset.conversionKind
        || (balance === 'Cartão-oferta' ? 'gift' : balance === 'Pontos e créditos' ? 'points' : '');
      const direction = selectedValue(directionInputs);
      const isGift = kind === 'gift';
      const isPoints = kind === 'points';
      const label = routeLabel(kind, direction);

      setConditionalField(cardField, cardInput, isGift, true);
      setConditionalField(accountField, accountInput, isPoints, true);
      setConditionalField(physicalField, physicalInput, isPoints, true);

      requirements.forEach((item) => {
        const type = item.dataset.conversionRequirement;
        item.hidden = (type === 'card' && !isGift)
          || ((type === 'account' || type === 'physical') && !isPoints);
      });

      if (routeInput) routeInput.value = balance && direction ? label : '';
      if (routeSummary) routeSummary.textContent = label;
      if (summarySubmit) summarySubmit.hidden = postedSuccessfully;

      const completed = [Boolean(balance), Boolean(balance && direction), false];
      progressItems.forEach((item, index) => {
        const isActive = index === (balance ? (direction ? 2 : 1) : 0);
        item.classList.toggle('is-complete', completed[index]);
        item.classList.toggle('is-active', isActive);
        if (isActive) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    };

    [...balanceInputs, ...directionInputs].forEach((input) => input.addEventListener('change', update));
    update();
  };

  const initReturns = (root) => {
    const intent = root.querySelector('[data-return-intent-select]');
    const target = root.querySelector('#troca-assistencia');

    root.querySelectorAll('[data-return-intent]').forEach((link) => {
      link.addEventListener('click', () => {
        if (intent) intent.value = link.dataset.returnIntent;
        window.setTimeout(() => target?.querySelector('h2')?.focus({ preventScroll: true }), 350);
      });
    });
  };

  const initFaq = (root) => {
    const search = root.querySelector('[data-faq-search]');
    const clear = root.querySelector('[data-faq-clear]');
    const filterButtons = [...root.querySelectorAll('[data-faq-filter]')];
    const groups = [...root.querySelectorAll('[data-faq-group]')];
    const items = [...root.querySelectorAll('[data-faq-item]')];
    const count = root.querySelector('[data-faq-count]');
    const empty = root.querySelector('[data-faq-empty]');
    let activeCategory = 'all';

    items.forEach((item) => {
      item.dataset.searchIndex = normalise(item.textContent);
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });

    const update = () => {
      const term = normalise(search?.value);
      let visibleCount = 0;

      items.forEach((item) => {
        const categoryMatch = activeCategory === 'all' || item.dataset.faqCategory === activeCategory;
        const searchMatch = !term || item.dataset.searchIndex.includes(term);
        const visible = categoryMatch && searchMatch;
        item.hidden = !visible;
        if (!visible) item.open = false;
        if (visible) visibleCount += 1;
      });

      groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
      });

      if (count) count.textContent = `${visibleCount} ${visibleCount === 1 ? count.dataset.resultOne : count.dataset.resultOther}`;
      if (empty) empty.hidden = visibleCount !== 0;
      if (clear) clear.hidden = !search?.value;
    };

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeCategory = button.dataset.faqFilter || 'all';
        filterButtons.forEach((candidate) => {
          candidate.setAttribute('aria-pressed', candidate.dataset.faqFilter === activeCategory ? 'true' : 'false');
        });
        update();
      });
    });

    search?.addEventListener('input', update);
    clear?.addEventListener('click', () => {
      if (!search) return;
      search.value = '';
      search.focus();
      update();
    });
    update();
  };

  const initRoot = (root) => {
    if (root.dataset.btServiceInitialised === 'true') return;
    root.dataset.btServiceInitialised = 'true';

    if (root.matches('[data-bt-contact]')) initContact(root);
    if (root.matches('[data-bt-conversion]')) initConversion(root);
    if (root.matches('[data-bt-returns]')) initReturns(root);
    if (root.matches('[data-bt-faq]')) initFaq(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-bt-service-root]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
